/**
 * IndexedDB persistence for the image editor's current session.
 * Falls back silently to no-op if IndexedDB is unavailable.
 *
 * NOTE: File System Access API handles (`FileSystemFileHandle`, kept on
 * `TabDoc.fileHandle` to enable in-place save) are intentionally NOT
 * persisted here. While handles are technically structuredClone-able and
 * IDB-storable in modern Chromium, restoring them on the next session
 * requires a fresh `queryPermission()` re-grant prompt — which feels
 * worse than the current behavior (Save degrades to a download in this
 * one edge case). If this becomes a frequent papercut, a follow-up PR can
 * add an `idb-keyval`-style handle store + a one-click re-grant UI.
 */

import type { Layer, EditorDocument } from '../types';
import { fromFlatLayers } from './layer-tree';

const DB_NAME = 'fias-image-editor';
const STORE_NAME = 'sessions';
/** v1 stored layers as a flat `Layer[]`. v2 stores the Tier 1
 *  `EditorDocument` tree under `document`. The v1 → v2 migration
 *  runs in-app on load (not in IndexedDB's onupgradeneeded) so the
 *  reducer's deserializer can use the same `fromFlatLayers` helper
 *  the runtime uses for new documents — no second source of truth.
 *  v1 records still load (just via the migration) so a user with
 *  pre-Tier-1 IndexedDB state doesn't lose their work. */
const DB_VERSION = 2;
const SESSION_KEY = 'current';

export interface PersistedSession {
  canvasJSON: object;
  fileName: string;
  fileId: string | null;
  /** Legacy: Fabric brightness/contrast/saturation filters from
   *  before the Adjustments dialog landed. New sessions don't
   *  write this (no UI to set the values); old sessions on read
   *  silently ignore it. Optional purely for back-compat with
   *  records persisted under the previous schema. */
  filters?: { brightness: number; contrast: number; saturation: number };
  /** Fabric viewport transform [a, b, c, d, e, f] — captures zoom + pan. */
  viewportTransform?: number[];
  /** v2: the full layer document. Authoritative shape. Older
   *  records persisted `layers` instead — `loadSession` migrates
   *  those records by promoting `layers` → a flat root-only
   *  document. New sessions only write `document`. */
  document?: EditorDocument;
  /** v1 (legacy): flat ordered layer list. Promoted to `document`
   *  on read. Newly-written sessions must NOT include this; the
   *  type stays optional only for the transitional read path. */
  layers?: Layer[];
  activeLayerId?: string | null;
  /** Undo/redo stack — each entry is the same shape as a `useCanvasHistory` snapshot. */
  historyStates?: { canvasJSON: object; extra?: unknown }[];
  /** Position within historyStates of the "current" state. */
  historyIndex?: number;
  savedAt: number;
}

interface StoredRecord extends PersistedSession {
  id: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadSession(): Promise<PersistedSession | null> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(SESSION_KEY);
      request.onsuccess = () => {
        const record = request.result as StoredRecord | undefined;
        if (record && typeof record.canvasJSON === 'object') {
          const { id: _id, ...session } = record;
          resolve(migrateSession(session));
        } else {
          resolve(null);
        }
      };
      request.onerror = () => {
        console.warn('[image-editor] loadSession failed:', request.error);
        resolve(null);
      };
    });
  } catch (err) {
    console.warn('[image-editor] loadSession threw:', err);
    return null;
  }
}

/** Promote a v1 (flat-array) session to v2 (document-tree).
 *  Already-v2 sessions pass through unchanged. The check is the
 *  presence of `document` rather than a version field — IndexedDB's
 *  schema version covers the object-store layout, but the record
 *  shape change is layer-data-only and benefits from the simpler
 *  in-app check. New writes always populate `document` only. */
function migrateSession(session: PersistedSession): PersistedSession {
  if (session.document) {
    // v2 record. Strip any vestigial `layers` field a half-written
    // intermediate session might carry.
    if (session.layers) {
      const { layers: _legacy, ...rest } = session;
      return rest;
    }
    return session;
  }
  if (session.layers && session.layers.length > 0) {
    // v1 record. Promote to a flat document, drop the legacy field.
    const { layers, ...rest } = session;
    return { ...rest, document: fromFlatLayers(layers) };
  }
  // Empty / unknown record — no layers to migrate.
  const { layers: _maybeEmpty, ...rest } = session;
  return rest;
}

export async function saveSession(session: PersistedSession): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const record: StoredRecord = { id: SESSION_KEY, ...session };
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.warn('[image-editor] saveSession failed:', request.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] saveSession threw:', err);
  }
}

export async function clearSession(): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(SESSION_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.warn('[image-editor] clearSession failed:', request.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] clearSession threw:', err);
  }
}
